import { useEffect, useMemo, useState, useCallback } from "react";
import DataTable from "../../components/common/DataTable";
import { getTransactions } from "../../services/transactionService";
import { getWalletDashboard } from "../../services/walletService";
import { handleApiError } from "../../utils/handleApiError";

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function TrendingUpIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) =>
  `\u20B9 ${toNumber(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getServiceName = (tx) =>
  tx.service_name || tx.service || tx.service_type || tx.transaction_type || tx.mode || "N/A";

const getTransactionId = (tx) => tx.transaction_id || tx.txn_id || tx.id || "-";

const statusStyle = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "SUCCESS") return "bg-emerald-100 text-emerald-600";
  if (s === "PENDING") return "bg-amber-100 text-amber-600";
  return "bg-red-100 text-red-600";
};

export default function Dashboard() {
  const [activeTransactionTab, setActiveTransactionTab] = useState("All");
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    has_more: false,
    total: 0,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [txError, setTxError] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    getWalletDashboard()
      .then((res) => {
        setWalletData(res.data?.data || res.data || null);
      })
      .catch(handleApiError)
      .finally(() => setLoadingWallet(false));
  }, []);

  const fetchTransactions = useCallback((pg, lmt, srch, serviceTab) => {
    setLoadingTransactions(true);
    setTxError(null);
    getTransactions({
      page: pg,
      limit: lmt,
      search: srch || undefined,
      service: serviceTab && serviceTab !== "All" ? serviceTab : undefined,
    })
      .then((res) => {
        const payload = res.data?.data || res.data || {};
        const rows = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.transactions)
            ? payload.transactions
            : Array.isArray(payload)
              ? payload
              : [];
        const p = payload?.pagination || payload;
        const currentPage = Number(p?.current_page) || pg;
        const lastPage = Number(p?.last_page) || 1;
        setTransactions(rows);
        setPagination({
          current_page: currentPage,
          last_page: lastPage,
          has_more: Boolean(p?.has_more ?? currentPage < lastPage),
          total: Number(p?.total) || rows.length,
        });
      })
      .catch((err) => { handleApiError(err); setTxError('Failed to load transactions.'); })
      .finally(() => setLoadingTransactions(false));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchTransactions(page, limit, search, activeTransactionTab);
  }, [page, limit, search, activeTransactionTab, fetchTransactions]);

  const handleLimitChange = (val) => {
    setLimit(val);
    setPage(1);
  };

  const columns = useMemo(
    () => [
      {
        id: "sr",
        header: "Sr No",
        size: 60,
        Cell: ({ row }) => (
          <span className="text-[13px] text-[#4C444E] font-semibold">
            {(page - 1) * limit + row.index + 1}
          </span>
        ),
      },
      {
        accessorKey: "transaction_id",
        header: "Transaction ID",
        Cell: ({ row }) => (
          <span className="text-[13px] font-bold text-[#4B2364]">{getTransactionId(row.original)}</span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Date & Time",
        Cell: ({ row }) => {
          const raw = row.original.created_at || row.original.date || null;
          const dt = raw ? new Date(raw) : null;
          return (
            <div>
              <div className="text-[13px] font-semibold text-[#1E0A3C]">
                {dt ? dt.toLocaleDateString() : "-"}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {dt ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "service_name",
        header: "Service Type",
        Cell: ({ row }) => <span className="text-[13px] font-bold text-[#1E0A3C]">{getServiceName(row.original)}</span>,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        Cell: ({ row }) => (
          <span className="text-sm font-bold text-[#1E0A3C]">{formatCurrency(row.original.amount)}</span>
        ),
      },
      {
        accessorKey: "commission",
        header: "Commission",
        Cell: ({ row }) => {
          const commission = row.original.commission ?? row.original.service_commission ?? 0;
          return (
            <span className={`text-[13px] font-bold ${toNumber(commission) > 0 ? "text-emerald-500" : "text-gray-500"}`}>
              {toNumber(commission) > 0 ? "+ " : ""}
              {formatCurrency(commission)}
            </span>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => (
          <span className={`px-3 py-1.5 rounded-full text-[11px] font-extrabold tracking-wide ${statusStyle(row.original.status)}`}>
            {String(row.original.status || "UNKNOWN").toUpperCase()}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        Cell: () => (
          <button className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
              <circle cx="5" cy="12" r="2" />
            </svg>
          </button>
        ),
      },
    ],
    [page, limit]
  );

  const creditWalletBalance = walletData?.credit_wallet_balance ?? walletData?.total_balance ?? 0;
  const debitWalletBalance = walletData?.debit_wallet_balance ?? walletData?.available_balance ?? 0;
  const inflowToday = walletData?.inflow_today ?? walletData?.today_inflow ?? 0;
  const outflowToday = walletData?.outflow_today ?? walletData?.today_outflow ?? 0;
  const flowBase = Math.max(toNumber(inflowToday) + toNumber(outflowToday), 1);
  const inflowWidth = Math.max(8, Math.round((toNumber(inflowToday) / flowBase) * 100));
  const outflowWidth = Math.max(8, Math.round((toNumber(outflowToday) / flowBase) * 100));

  return (
    <div className="flex-1 px-8 py-7 overflow-y-auto">
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div className="rounded-[12px] p-6 text-white relative overflow-hidden shadow-[0px_20px_40px_0px_#4B236426] bg-gradient-to-br from-[#754F89] to-[#4B2364]">
          <div className="absolute -right-2.5 -top-2.5 w-24 h-24 border-[20px] border-white/5 rounded-2xl" />
          <div className="absolute right-14 top-10 w-14 h-14 border-[15px] border-white/[0.03] rounded-xl" />
          <div className="relative z-10">
            <div className="text-[14px] font-medium text-[#F3E8FFCC] mb-2">Credit Wallet Balance</div>
            <div className="text-[36px] font-extrabold mb-6">
              {loadingWallet ? "..." : formatCurrency(creditWalletBalance)}
            </div>
            <div className="flex items-center gap-1.5 text-emerald-400 text-[13px] font-semibold">
              <TrendingUpIcon /> Wallet API: /wallet/dashboard
            </div>
          </div>
        </div>

        <div className="bg-white rounded-[12px] p-6 relative overflow-hidden shadow-[0px_24px_32px_0px_#4B23640A]">
          <div className="absolute -right-7 -top-5 w-28 h-28 bg-gray-100/60 rounded-3xl rotate-[15deg]" />
          <div className="relative z-10">
            <div className="text-[14px] font-semibold text-[#4C444E] mb-2">Debit Wallet Balance</div>
            <div className="text-[36px] font-extrabold text-[#34094D] mb-6">
              {loadingWallet ? "..." : formatCurrency(debitWalletBalance)}
            </div>
            <div className="flex items-center gap-1.5 text-[#4C444E] text-[12px] font-medium">
              <ClockIcon /> Updated from live dashboard data
            </div>
          </div>
        </div>

        <div className="bg-[#F3F4F5] rounded-[12px] p-6 ">
          <div className="text-[18px] font-bold text-[#34094D] mb-6">Statement Summary</div>
          <div className="mb-5">
            <div className="flex justify-between text-[13px] font-semibold mb-2">
              <span className="text-[#4C444E]">Inflow (Today)</span>
              <span className="text-[#059669]">{loadingWallet ? "..." : `+ ${formatCurrency(inflowToday)}`}</span>
            </div>
            <div className="w-full h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
              <div
                className="h-2 bg-[linear-gradient(91.92deg,_#EA8534_0%,_#A0111A_100%)] rounded-full"
                style={{ width: `${inflowWidth}%` }}
              />
            </div>
          </div>
          <div className="mb-6">
            <div className="flex justify-between text-[13px] font-semibold mb-2">
              <span className="text-[#4C444E]">Outflow (Today)</span>
              <span className="text-[#BA1A1A]">{loadingWallet ? "..." : `- ${formatCurrency(outflowToday)}`}</span>
            </div>
            <div className="w-full h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
              <div
                className="h-full bg-[linear-gradient(94.47deg,_#754F89_0%,_#4B2364_100%)]"
                style={{ width: `${outflowWidth}%` }}
              />
            </div>
          </div>
          <div className="text-center">
            <button className="text-[#34094D] text-[13px] font-semibold border-none bg-transparent cursor-default">
              View Full Report
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] mb-6">
        <div className="flex justify-between p-8 items-start flex-wrap gap-4">
          <div>
            <h2 className="text-[24px] font-bold text-[#34094D] mb-1">Transaction Ledger</h2>
            <p className="text-sm text-[#4C444E]">Real-time records for all service operations</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/></svg>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search transactions..."
                className="bg-[#F3F4F5] rounded-[10px] pl-8 pr-4 py-2 text-[13px] text-[#191C1D] border-none outline-none w-48"
              />
            </div>
            {/* Limit */}
            <select
              value={limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="bg-[#F3F4F5] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none cursor-pointer"
            >
              {[10, 25, 50].map((l) => <option key={l} value={l}>Show {l}</option>)}
            </select>
          </div>
        </div>

        {txError ? (
          <div className="text-center py-10 text-red-500 text-sm">{txError}</div>
        ) : (
          <>
            {loadingTransactions && (
              <div className="px-8 pb-3 text-xs text-gray-500">
                {transactions.length > 0 ? "Refreshing data..." : "Loading transactions..."}
              </div>
            )}
            <DataTable columns={columns} data={transactions} />
            {!loadingTransactions && transactions.length === 0 && (
              <div className="text-center py-10 text-gray-400 text-sm">No data found</div>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
        <span className="text-xs text-[#4C444E]">
          {pagination.total > 0
            ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, pagination.total)} of ${pagination.total} records`
            : "Showing 0 records"}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${
              page === 1 ? "bg-gray-50 text-gray-300 cursor-not-allowed" : "bg-white text-[#4C444E] hover:bg-gray-50"
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className="text-xs text-[#4C444E]">
            {pagination.current_page} / {pagination.last_page}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!pagination.has_more}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${
              !pagination.has_more
                ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                : "bg-white text-[#4C444E] hover:bg-gray-50"
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
        <div className="flex items-center gap-4 border-r border-gray-200">
          <div className="w-12 h-12 rounded-full bg-[linear-gradient(135deg,#754F89_0%,#4B2364_100%)] flex items-center justify-center text-white">
            <TrendingUpIcon />
          </div>
          <div>
            <div className="text-[11px] font-bold text-[#4C444E] tracking-wide mb-1">TOTAL COMMISSION (MTD)</div>
            <div className="text-[24px] font-extrabold text-[#34094D]">{formatCurrency(walletData?.mtd_commission || 0)}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 pl-8 border-r border-gray-200">
          <div className="w-12 h-12 rounded-full bg-[linear-gradient(135deg,#EA8534_0%,#A0111A_100%)] flex items-center justify-center text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
              <rect x="9" y="9" width="6" height="6" />
            </svg>
          </div>
          <div>
            <div className="text-[11px] font-bold text-[#4C444E] tracking-wide mb-1">ACTIVE TERMINAL COUNT</div>
            <div className="text-[24px] font-extrabold text-[#34094D]">{walletData?.active_terminal_count || 0}</div>
          </div>
        </div>
        <div className="flex flex-col justify-center items-end">
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#059669] mb-1">
            <div className="w-2 h-2 rounded-full bg-[#059669]" /> SYSTEMS OPERATIONAL
          </div>
          <div className="text-xs text-gray-500">Data source: live APIs</div>
        </div>
      </div>
    </div>
  );
}
